import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, ArrowRight, Plus, X } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { publicStarterDeck } from '../../services/api';
import type { FrameworkField, FrameworkView } from '../../types/aai';
import {
  Action, ErrorNote, Field, Input, Kicker, Loading, Modal, Panel, Select,
} from '../../components/aa';
import { IntakeCard, IntakeRail } from '../../components/framework/IntakeRail';

/*
 * The opening questions: the cards every client answers before Orbit thinks.
 *
 * One half of a Solution Partner's framework control. The intake underneath,
 * where a question's key, type and validation live, is the other half and sits
 * on the next tab. The page that hosts both owns the scope and the chrome, so
 * this renders as a section and never as a page of its own.
 *
 * What this screen controls is which questions open the conversation and in
 * what order, because that is the partner's method. Two versions sit side by
 * side: the Agents Anywhere default, which is read-only and carries the
 * platform's name, and theirs, which every client they add inherits. With
 * ?client_id= the second version is that one client's instead, so a single
 * engagement can ask something the partner's other clients are not asked.
 *
 * Six is the cap, and a partner's own question REPLACES one of the six rather
 * than stacking on top: past six the opening stops being a conversation and
 * starts being a form, which is the thing the deck exists to avoid. So adding
 * at the cap is refused and the way through is to drop one first.
 */

interface DefaultCard { key: string; label: string; why: string; section: string; }

const givesLine = (why: string | null, label: string): string => {
  const text = (why || '').trim();
  if (text) return text;
  return `Gives Orbit ${label.toLowerCase()}`;
};

/* The same card the intake tab deals, so the six a client sees first are read
   the same way as every question behind them. The deck is the shape of this
   thing: six cards in the order they are dealt, not six rows in a table. */
const QuestionCard: React.FC<{
  index: number;
  label: string;
  why: string;
  owner: 'platform' | 'partner';
  controls?: React.ReactNode;
}> = ({ index, label, why, owner, controls }) => (
  <IntakeCard
    rank={index + 1}
    label={label}
    why={`Gives Orbit · ${why}`}
    critical={false}
    hidden={false}
    badge={{ text: owner === 'partner' ? 'your question' : 'super admin', tone: owner === 'partner' ? 'warn' : 'neutral' }}
    actions={<div className="flex shrink-0 items-center gap-1.5">{controls}</div>}
  />
);

const AddQuestion: React.FC<{
  view: FrameworkView;
  onPick: (key: string) => Promise<void>;
  onCreate: (spec: { label: string; why: string; kind: string; section: string }) => Promise<void>;
  onClose: () => void;
}> = ({ view, onPick, onCreate, onClose }) => {
  const [mode, setMode] = useState<'pool' | 'new'>('pool');
  const [pick, setPick] = useState('');
  const [label, setLabel] = useState('');
  const [why, setWhy] = useState('');
  const [kind, setKind] = useState('text');
  /* A section of a switched-off intake swallows any question put in it, so
     only live sections are offered here. */
  const platformOnly = view.platform_only_sections || [];
  const liveSections = view.section_order.filter((s) => !view.hidden_sections.includes(s) && !platformOnly.includes(s));
  const [section, setSection] = useState(liveSections[0] || '');
  const [busy, setBusy] = useState(false);

  const pool = view.fields.filter((f) => !f.hidden && !(view.platform_only_sections || []).includes(f.section) && !view.starter.keys.includes(f.key));

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'pool') {
        if (!pick) {
          toast.info('Choose a question first.');
          return;
        }
        await onPick(pick);
      } else {
        if (label.trim().length < 4) {
          toast.info('Write the question first.');
          return;
        }
        if (!section) {
          toast.info('Your framework has no intake to hold a question yet. Upload documents or bring one of ours back under The intake.');
          return;
        }
        await onCreate({ label: label.trim(), why: why.trim(), kind, section });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} context="Settings · framework">
      <div className="grid gap-4">
        <h2 className="m-0 text-[18px] font-bold text-ink">Add a question</h2>
        <div className="flex gap-2">
          <Action size="sm" variant={mode === 'pool' ? undefined : 'quiet'} onClick={() => setMode('pool')}>One Orbit already knows</Action>
          <Action size="sm" variant={mode === 'new' ? undefined : 'quiet'} onClick={() => setMode('new')}>Write my own</Action>
        </div>
        {mode === 'pool' && (
          <Field label="Question" hint="Anything already in the framework can open the conversation instead of being asked later.">
            <Select value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Pick a question</option>
              {pool.map((f) => <option key={f.key} value={f.key}>{view.sections[f.section] || f.section} · {f.label}</option>)}
            </Select>
          </Field>
        )}
        {mode === 'new' && (
          <>
            <Field label="The question, in your words"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What is the most draining part of your week?" autoFocus /></Field>
            <Field label="What it gives Orbit" hint="One line. It is shown under the question here, and it is how Orbit decides what the answer is for.">
              <textarea
                rows={2}
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                placeholder="The relief question: where automation would be felt first."
                className="w-full rounded-lg border border-line-strong bg-white px-3 py-2 text-[13px] text-ink"
              />
            </Field>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Answer type">
                <Select value={kind} onChange={(e) => setKind(e.target.value)}>
                  {view.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
                </Select>
              </Field>
              <Field label="Section">
                <Select value={section} onChange={(e) => setSection(e.target.value)}>
                  {liveSections.length === 0 && <option value="">No intake yet</option>}
                  {liveSections.map((s) => <option key={s} value={s}>{view.sections[s] || s}</option>)}
                </Select>
              </Field>
            </div>
          </>
        )}
        <div className="flex gap-2">
          <Action onClick={submit} disabled={busy} reason="Saving">Add to the opening</Action>
          <Action variant="quiet" onClick={onClose}>Cancel</Action>
        </div>
      </div>
    </Modal>
  );
};

export const OpeningQuestions: React.FC<{
  /** The client whose opening this is; without one it is the partner's own version. */
  clientId?: string;
  clientName?: string;
  /** On the Agents Anywhere framework: ours, shown and not changed. */
  readOnly?: boolean;
}> = ({ clientId: fixedClient, clientName: fixedName, readOnly = false }) => {
  const [params] = useSearchParams();
  const clientId = fixedClient || params.get('client_id') || '';
  const clientName = fixedName || params.get('client_name') || '';

  const [which, setWhich] = useState<'default' | 'mine'>('mine');
  const [view, setView] = useState<FrameworkView | null>(null);
  const [defaults, setDefaults] = useState<DefaultCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [mine, platform] = await Promise.all([
        aaiApi.getFramework('dossier', clientId),
        publicStarterDeck().catch(() => null),
      ]);
      setView(mine.data);
      if (platform) setDefaults(platform.cards.map((c) => ({ key: c.key, label: c.label, why: c.why, section: c.section })));
    } catch {
      setError('Could not load the self-register questions.');
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const cards = useMemo<FrameworkField[]>(() => {
    if (!view) return [];
    const byKey = new Map(view.fields.map((f) => [f.key, f]));
    return view.starter.keys.map((k) => byKey.get(k)).filter((f): f is FrameworkField => Boolean(f));
  }, [view]);

  const save = async (key: string, op: string, spec: Record<string, unknown>) => {
    setSaving(true);
    try {
      const res = await aaiApi.putFramework('dossier', key, { op, spec, client_id: clientId });
      setView(res.data);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || 'Could not save that change.');
    } finally {
      setSaving(false);
    }
  };

  const move = async (key: string, direction: -1 | 1) => {
    if (!view) return;
    const order = [...view.starter.keys];
    const from = order.indexOf(key);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= order.length) return;
    order.splice(from, 1);
    order.splice(to, 0, key);
    setSaving(true);
    try {
      for (let rank = 0; rank < order.length; rank += 1) {
        // Ranks are written one at a time on purpose: the server copies an
        // inherited deck to this scope on the first write, and a parallel burst
        // would race that copy.
        // eslint-disable-next-line no-await-in-loop
        const res = await aaiApi.putFramework('dossier', order[rank], { op: 'starter', spec: { rank: rank + 1 }, client_id: clientId });
        setView(res.data);
      }
    } catch {
      toast.error('Could not reorder the questions.');
    } finally {
      setSaving(false);
    }
  };

  const drop = async (key: string) => {
    setSaving(true);
    try {
      const res = await aaiApi.deleteFramework('dossier', key, clientId, 'starter');
      setView(res.data);
      toast.success('Dropped from the opening. Orbit still asks it later if it matters.');
    } catch {
      toast.error('Could not drop that question.');
    } finally {
      setSaving(false);
    }
  };

  const addExisting = async (key: string) => {
    if (!view) return;
    await save(key, 'starter', { rank: view.starter.keys.length + 1 });
  };

  const addNew = async (spec: { label: string; why: string; kind: string; section: string }) => {
    if (!view) return;
    const suffix = spec.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30) || `q${Date.now()}`;
    const key = `${spec.section}.${suffix}`;
    await save(key, 'add', { section: spec.section, label: spec.label, kind: spec.kind, why: spec.why });
    await save(key, 'starter', { rank: view.starter.keys.length + 1 });
  };

  if (error) return <ErrorNote message={error} />;
  if (!view) return <Loading label="Loading the self-register questions" />;

  const mineLabel = clientId ? `This client only${clientName ? `: ${clientName}` : ''}` : 'Your version, Solution Partner';
  const inherited = view.starter.scope !== view.scope_kind;
  const full = cards.length >= view.starter.limit;

  /* What this tab is for, before any of its cards. */
  const brief = (
    <div className="mb-5 rounded-[13px] border border-line bg-[var(--aa-card)] px-4 py-3 text-[13px] leading-[20px] text-ink">
      The six questions every client&rsquo;s <b>Owner</b> answers first, whether they register through your link or you add
      them. They are set by Agents Anywhere and always asked, in this order. After them the Owner goes on to the intake.
    </div>
  );

  if (readOnly) {
    return (
      <>
        {brief}
        <IntakeRail head={<Kicker>Always asked of the Owner, in this order</Kicker>}>
          {defaults.map((c, i) => (
            <QuestionCard key={c.key} index={i} label={c.label} why={givesLine(c.why, c.label)} owner="platform" />
          ))}
        </IntakeRail>
        {defaults.length === 0 && <Panel><div className="px-5 py-6 text-[13px] text-ink-dim">The opening questions could not be read just now.</div></Panel>}
        <p className="mt-4 text-[12.5px] leading-[20px] text-ink-dim">
          These six are fixed: they cannot be moved, dropped or reworded. Change what else Orbit asks under The intake.
        </p>
      </>
    );
  }

  return (
    <>
      {brief}
      <div className="mb-5 flex w-max gap-1 rounded-card border border-line bg-white p-1">
        <button
          type="button"
          onClick={() => setWhich('default')}
          className={`rounded-lg px-4 py-2 text-[13px] font-semibold ${which === 'default' ? 'bg-ink text-white' : 'text-ink-muted hover:bg-canvas-inset'}`}
        >
          Agents Anywhere default
        </button>
        <button
          type="button"
          onClick={() => setWhich('mine')}
          className={`rounded-lg px-4 py-2 text-[13px] font-semibold ${which === 'mine' ? 'bg-ink text-white' : 'text-ink-muted hover:bg-canvas-inset'}`}
        >
          {mineLabel}
        </button>
      </div>

      {which === 'default' && (
        <>
          <IntakeRail head={<Kicker>Asked when a customer self-registers</Kicker>}>
            {defaults.map((c, i) => (
              <QuestionCard key={c.key} index={i} label={c.label} why={givesLine(c.why, c.label)} owner="platform" />
            ))}
          </IntakeRail>
          {defaults.length === 0 && <Panel><div className="px-5 py-6 text-[13px] text-ink-dim">The platform default could not be read just now.</div></Panel>}
        </>
      )}

      {which === 'mine' && (
        <>
          {/* Left to right in the order they are dealt, so the arrows on a card
              move it through the deck rather than up and down a list. */}
          <IntakeRail
            head={(
              <>
                <Kicker>Asked at self-register, in order</Kicker>
                <Action
                  size="sm"
                  variant="quiet"
                  icon={<Plus size={14} />}
                  disabled={full || saving}
                  reason={full ? `${view.starter.limit} questions is the limit. Drop one to make room for yours.` : 'Saving'}
                  onClick={() => setAdding(true)}
                >
                  Add a question
                </Action>
                {full && (
                  <span className="text-[12px] text-ink-dim">
                    Full at {view.starter.limit}. Drop one to put yours in its place.
                  </span>
                )}
              </>
            )}
          >
            {cards.map((f, i) => {
              const owner = f.added && f.provenance !== 'global' ? 'partner' : 'platform';
              return (
                <QuestionCard
                  key={f.key}
                  index={i}
                  label={f.label}
                  why={givesLine(f.why, f.label)}
                  owner={owner}
                  controls={(
                    <>
                      <button type="button" aria-label="Move earlier" disabled={saving || i === 0} onClick={() => move(f.key, -1)} className="grid h-7 w-7 place-items-center rounded-md border border-line text-ink-muted disabled:opacity-40 hover:border-ink hover:text-ink"><ArrowLeft size={13} /></button>
                      <button type="button" aria-label="Move later" disabled={saving || i === cards.length - 1} onClick={() => move(f.key, 1)} className="grid h-7 w-7 place-items-center rounded-md border border-line text-ink-muted disabled:opacity-40 hover:border-ink hover:text-ink"><ArrowRight size={13} /></button>
                      <button type="button" aria-label="Drop" disabled={saving} onClick={() => drop(f.key)} className="grid h-7 w-7 place-items-center rounded-md border border-line text-ink-muted disabled:opacity-40 hover:border-danger hover:text-danger"><X size={13} /></button>
                    </>
                  )}
                />
              );
            })}
          </IntakeRail>
          {cards.length === 0 && <Panel><div className="px-5 py-6 text-[13px] text-ink-dim">No self-register questions set. Add one above.</div></Panel>}
          <p className="mt-4 text-[12.5px] leading-[20px] text-ink-dim">
            Up to {view.starter.limit} questions, so registering stays quick: a question of yours takes the place of one of these. Anything
            you drop is still asked later, in the intake, when it matters.
            {inherited && ' This list is still the default: your first change makes it your own.'}
          </p>
        </>
      )}

      {adding && <AddQuestion view={view} onPick={addExisting} onCreate={addNew} onClose={() => setAdding(false)} />}
    </>
  );
};

export default OpeningQuestions;
